/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/types/user.ts
 * PURPOSE: Component implementation aligned with SDLC 6.3.1 standards
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Type Definitions, API Contracts, Vietnamese F&B Data Models
 * - Impact: Type Safety, API Consistency, Developer Experience
 * - Stakeholders: Development Team, Backend Team, Frontend Team
 *
 * @technical-architecture
 * - Layer: Type System, Data Contracts
 * - Pattern: TypeScript Interfaces, Type Guards, Schema Validation
 * - Dependencies: TypeScript, Zod (optional)
 */

/**
 * User roles - matches backend UserRole enum exactly
 * @see backend/app/schemas/user.py
 * @deprecated Legacy vocabulary. The create-user UI binds directly to
 * TenantUserRole (SPEC-0208 §2.3/§3.2, DD-0056 §6.6). Retained only for
 * UserResponse/UserUpdate compatibility until those are reconciled separately.
 */
export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'SUPERVISOR' | 'EMPLOYEE' | 'VIEWER';

/**
 * Canonical tenant role vocabulary — mirrors backend TenantUserRole enum
 * verbatim (backend/app/models/tenant_user.py). The create-user UI sends this
 * value as `role` with NO mapping table (SPEC-0208 §3.2 bind-direct clause).
 * Excludes TENANT_OWNER (triage/CTO-gated, BR-TENM-011 last-owner); no
 * SUPER_ADMIN exists in the tenant role enum.
 */
export type TenantUserRole =
  | 'TENANT_ADMIN'
  | 'CORPORATE_MANAGER'
  | 'BRANCH_MANAGER'
  | 'SHIFT_MANAGER'
  | 'HEAD_CHEF'
  | 'CHEF'
  | 'WAITER'
  | 'CASHIER'
  | 'KITCHEN_STAFF'
  | 'DELIVERY_STAFF'
  | 'CLEANER'
  | 'TRAINEE'
  | 'EMPLOYEE';

/**
 * SINGLE source of truth for the role dropdown: the 13 canonical creatable
 * TenantUserRole values with Vietnamese labels (SPEC-0208 §3.2). Future enum
 * additions are a one-line change here. Order = display order; EMPLOYEE is the
 * form default. TENANT_OWNER deliberately omitted (SPEC-0208 §6.6).
 *
 * CORPORATE_MANAGER (2026-08-28, @ba): nhãn cũ "Quản lý cấp tập đoàn" lệch
 * chuẩn platform-wide SSOT (docs/02-design/04-ui-ux-design/UI-LABELS-VI-ENUM.md
 * §5) — cùng lúc 2 file khác (ManagerLayout.tsx, ManagerProfilePage.tsx) lại
 * ghi "Quản lý vùng", 3 nhãn khác nhau cho cùng 1 role. CHỐT "Quản lý phòng
 * ban" (người yêu cầu xác nhận trực tiếp), đồng bộ cả 3 nơi.
 */
export const CREATABLE_TENANT_ROLES: ReadonlyArray<{
  value: TenantUserRole;
  label: string;
}> = [
  { value: 'TENANT_ADMIN', label: 'Quản trị viên' },
  { value: 'CORPORATE_MANAGER', label: 'Quản lý phòng ban' },
  { value: 'BRANCH_MANAGER', label: 'Quản lý chi nhánh' },
  { value: 'SHIFT_MANAGER', label: 'Quản lý ca' },
  { value: 'HEAD_CHEF', label: 'Bếp trưởng' },
  { value: 'CHEF', label: 'Đầu bếp' },
  { value: 'WAITER', label: 'Nhân viên phục vụ' },
  { value: 'CASHIER', label: 'Thu ngân' },
  { value: 'KITCHEN_STAFF', label: 'Nhân viên bếp' },
  { value: 'DELIVERY_STAFF', label: 'Nhân viên giao hàng' },
  { value: 'CLEANER', label: 'Nhân viên vệ sinh' },
  { value: 'TRAINEE', label: 'Thực tập sinh' },
  { value: 'EMPLOYEE', label: 'Nhân viên' },
];

/**
 * Roles for which organization_id (anchor) is NOT required. Per
 * chk_tenant_users_anchor_required (SPEC-0208 §3.3 / DD-0051), only
 * TENANT_ADMIN may be created without an org anchor; all other 12 roles
 * require one to avoid a 422 E_ANCHOR_REQUIRED round-trip.
 */
export const ANCHOR_OPTIONAL_ROLES: ReadonlyArray<TenantUserRole> = ['TENANT_ADMIN'];

/**
 * True when the given role requires an organization_id anchor at create time.
 */
export function roleRequiresAnchor(role: TenantUserRole): boolean {
  return !ANCHOR_OPTIONAL_ROLES.includes(role);
}

export interface UserBase {
  email: string;
  phone_number?: string | null;
  full_name: string;
}

/**
 * User creation schema - matches backend UserCreate
 * @see backend/app/schemas/user.py
 */
export interface UserCreate extends UserBase {
  password: string;
  role: UserRole;
  full_name_vietnamese?: string;
  position?: string;
  organization_id?: string;
  branch_id?: string;
  department_id?: string;
  employee_code?: string;
  language_preference?: string;
}

/**
 * Tenant user creation schema — canonical create payload (SPEC-0208 §2.3).
 * Mirrors backend TenantUserCreate (backend/app/api/v1/tenant_users.py).
 * Sent to POST /api/v1/tenant-users/create. `username` is REQUIRED
 * (^[a-zA-Z0-9_-]+$, 3-100). `role` must be a valid TenantUserRole value.
 * @see backend/app/api/v1/tenant_users.py TenantUserCreate
 */
export interface TenantUserCreate {
  username: string;
  password: string;
  full_name: string;
  full_name_vietnamese?: string;
  phone_number?: string;
  email?: string;
  employee_code?: string;
  role?: string;
  branch_id?: string;
  organization_id?: string;
  department?: string;
  position?: string;
  hire_date?: string;
  contract_type?: string;
  salary_type?: string;
  address?: string;
  city?: string;
  district?: string;
  ward?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  language_preference?: string;
}

export interface UserUpdate {
  email?: string;
  phone_number?: string | null;
  full_name?: string;
  full_name_vietnamese?: string;
  role?: UserRole;
  position?: string;
  branch_id?: string;
  department_id?: string;
  language_preference?: string;
  password?: string;
  is_active?: boolean;
}

export interface UserResponse extends UserBase {
  id: string;
  role: UserRole;
  full_name_vietnamese?: string | null;
  employee_code?: string | null;
  position?: string | null;
  organization_id?: string | null;
  branch_id?: string | null;
  department_id?: string | null;
  is_active: boolean;
  is_verified?: boolean;
  language_preference?: string;
  created_at: string;
  last_login?: string | null;
}

export interface UserListResponse {
  users: UserResponse[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Team Members Response (ADR-004)
 * @see docs/02-design/18-adrs/ADR-team-members-endpoint.md
 */
export interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
  avatar_url?: string | null;
  is_active: boolean;
  employee_code?: string | null;
}

export interface TeamMembersResponse {
  members: TeamMember[];
  total: number;
  branch_id: string;
  branch_name?: string | null;
}
